import React, { useState } from 'react';
import { Upload, X, FileText, FileSpreadsheet, File as FileIcon, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'csv', 'txt'];
const MAX_SIZE = 15 * 1024 * 1024; // 15MB

const getIcon = (urlOrName) => {
  if (!urlOrName) return FileIcon;
  const ext = urlOrName.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
  if (['pdf'].includes(ext)) return FileText;
  if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['doc', 'docx', 'odt', 'txt'].includes(ext)) return FileText;
  return FileIcon;
};

const isImage = (urlOrName) => {
  if (!urlOrName) return false;
  const ext = urlOrName.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

const DocumentUpload = ({ value, onChange, disabled = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const processFile = async (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Tipo de archivo no permitido. Use: ${ALLOWED_EXT.join(', ')}`);
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Archivo demasiado grande. Máximo 15MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/uploads/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange({ url: res.data.url, filename: res.data.filename, originalName: file.name });
      toast.success('Archivo subido correctamente');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); if (!disabled) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  // Normalize value: can be a string (legacy URL/dataURL) or an object {url, originalName}
  const fileUrl = typeof value === 'string' ? value : value?.url;
  const fileName = typeof value === 'object' ? (value?.originalName || value?.filename) : null;
  const Icon = getIcon(fileName || fileUrl);
  const showPreview = fileUrl && isImage(fileName || fileUrl);

  return (
    <div className="w-full">
      <input
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.csv,.txt"
        onChange={handleFileChange}
        disabled={disabled || uploading}
        className="hidden"
        id="document-upload-input"
        data-testid="document-upload-input"
      />

      {fileUrl ? (
        <div className="border-2 border-brand-200 rounded-xl p-4 bg-white">
          <div className="flex items-center gap-3">
            {showPreview ? (
              <img src={fileUrl.startsWith('data:') || fileUrl.startsWith('http') ? fileUrl : `${API}${fileUrl}`} alt="preview" className="w-16 h-16 object-cover rounded border" />
            ) : (
              <div className="w-16 h-16 rounded bg-brand-50 flex items-center justify-center flex-shrink-0">
                <Icon className="w-8 h-8 text-brand-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{fileName || 'Archivo subido'}</p>
              <a
                href={fileUrl.startsWith('http') ? fileUrl : `${API}${fileUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-600 hover:underline"
              >
                Abrir / Descargar
              </a>
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={disabled}
              data-testid="doc-upload-remove"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <label
            htmlFor="document-upload-input"
            className={`mt-3 block text-center text-sm text-brand-600 hover:text-brand-700 font-medium ${
              disabled || uploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          >
            {uploading ? 'Subiendo…' : 'Reemplazar archivo'}
          </label>
        </div>
      ) : (
        <label
          htmlFor="document-upload-input"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative block w-full
            border-2 border-dashed rounded-xl py-8 px-4
            transition-all duration-200
            ${isDragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}
            ${disabled || uploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          `}
        >
          <div className="flex flex-col items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center mb-3">
              <Upload className="w-7 h-7 text-brand-600" />
            </div>
            <p className="text-sm font-medium text-gray-700 text-center mb-1">
              {uploading ? 'Subiendo…' : isDragging ? 'Soltá el archivo aquí' : 'Click o arrastra un archivo'}
            </p>
            <p className="text-xs text-gray-400 text-center">
              PDF, Word, Excel, imágenes (jpg/png) — hasta 15MB
            </p>
          </div>
        </label>
      )}
    </div>
  );
};

export default DocumentUpload;
